import React from 'react';
import { Amount } from '@akropolis-web/primitives';
import { Tooltip } from '@mui/material';

import { MRT_ColumnDef, ToggleLowSupplyFilterButton, amountToAccessorValue } from 'components/MRT';
import { FallbackedTotalUSDWithProgress, FormattedAmount, TokenName } from 'components';
import { FormattedAmountProps } from 'components/FormattedAmount/types';
import { SystemStyleObject, styled } from 'core/styles';
import { TagBox } from 'components/TagBox';
import { isZero } from 'domain/utils';

import { MarketStatsData } from '../../types';

const headCellAlignStyles: SystemStyleObject = {
  '& .Mui-TableHeadCell-Content': {
    justifyContent: 'flex-end',
  },
};

const numberFilters = ['greaterThan', 'lessThan', 'between'];

const shortFormattedAmountProps: Omit<FormattedAmountProps<Amount>, 'sum'> = {
  precision: 0,
};

export const totalTableColumns: MRT_ColumnDef<MarketStatsData>[] = [
  {
    header: 'Asset',
    accessorFn: originalRow => originalRow.token.symbol,
    id: 'asset',
    enableColumnActions: false,
    columnFilterModeOptions: ['fuzzy', 'contains', 'startsWith', 'endsWith'],
    size: 115,
    Cell: ({ row, renderedCellValue }) => (
      <TokenNameBox>
        <TokenName token={row.original.token} symbol={renderedCellValue} />
        {isZero(row.original.debt.amount) &&
          row.original.borrowAPY &&
          row.original.borrowAPY.isZero() && (
            <Tooltip
              title={
                <>
                  <TooltipTitle>Collateral only</TooltipTitle>
                  <TooltipDescription>
                    Deposits that cannot be lent to others and can only be used as collateral for
                    one’s own loans
                  </TooltipDescription>
                </>
              }
            >
              <TagBox>CO</TagBox>
            </Tooltip>
          )}
      </TokenNameBox>
    ),
  },
  {
    header: 'Utilization',
    accessorFn: originalRow => amountToAccessorValue(originalRow.utilizationRatio),
    id: 'utilizationRatio',
    columnFilterModeOptions: numberFilters,
    enableColumnActions: false,
    sortingFn: 'basic',
    minSize: 100,
    maxSize: 125,
    filterFn: numberFilters[0],
    Cell: ({ row }) => (
      <FormattedAmount sum={row.original.utilizationRatio} precision={0} symbolSize="inherit" />
    ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Supply APY',
    accessorFn: originalRow => amountToAccessorValue(originalRow.supplyAPY),
    id: 'supplyAPY',
    columnFilterModeOptions: numberFilters,
    enableColumnActions: false,
    sortingFn: 'basic',
    minSize: 100,
    maxSize: 125,
    filterFn: numberFilters[0],
    Cell: ({ row }) =>
      row.original.supplyAPY && !row.original.supplyAPY.isZero() ? (
        <FormattedAmount sum={row.original.supplyAPY} precision={1} symbolSize="inherit" />
      ) : (
        '–'
      ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Borrow APY',
    accessorFn: originalRow => amountToAccessorValue(originalRow.borrowAPY),
    id: 'borrowAPY',
    columnFilterModeOptions: numberFilters,
    enableColumnActions: false,
    sortingFn: 'basic',
    minSize: 100,
    maxSize: 125,
    filterFn: numberFilters[0],
    Cell: ({ row }) =>
      row.original.borrowAPY && !row.original.borrowAPY.isZero() ? (
        <FormattedAmount sum={row.original.borrowAPY} precision={1} symbolSize="inherit" />
      ) : (
        '–'
      ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Variable Borrow APY',
    accessorFn: originalRow => amountToAccessorValue(originalRow.variableBorrowAPY),
    id: 'variableBorrowAPY',
    columnFilterModeOptions: numberFilters,
    enableColumnActions: false,
    sortingFn: 'basic',
    minSize: 100,
    maxSize: 125,
    filterFn: numberFilters[0],
    Cell: ({ row }) =>
      row.original.variableBorrowAPY ? (
        <FormattedAmount sum={row.original.variableBorrowAPY} precision={1} symbolSize="inherit" />
      ) : (
        '–'
      ),
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Total Liquidity',
    accessorFn: originalRow => amountToAccessorValue(originalRow.deposit.amount.value),
    id: 'totalTvl',
    columnFilterModeOptions: numberFilters,
    enableColumnActions: false,
    sortingFn: 'basic',
    minSize: 215,
    maxSize: 245,
    filterFn: numberFilters[0],
    Cell: ({ row, table }) => {
      return (
        <FallbackedTotalUSDWithProgress
          {...row.original.deposit.amount}
          formattedAmountProps={shortFormattedAmountProps}
          progress={row.original.deposit.progress}
          displayTokenAmounts={
            table.getState().displaySettings?.fallbackedTotalUSDVariant === 'fallback'
          }
        />
      );
    },
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Total Borrows',
    accessorFn: originalRow => amountToAccessorValue(originalRow.debt.amount.value),
    id: 'totalBorrows',
    columnFilterModeOptions: numberFilters,
    enableColumnActions: false,
    sortingFn: 'basic',
    minSize: 215,
    maxSize: 245,
    filterFn: numberFilters[0],
    Cell: ({ row, table }) => {
      return (
        <FallbackedTotalUSDWithProgress
          {...row.original.debt.amount}
          formattedAmountProps={shortFormattedAmountProps}
          progress={row.original.debt.progress}
          displayTokenAmounts={
            table.getState().displaySettings?.fallbackedTotalUSDVariant === 'fallback'
          }
        />
      );
    },
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  {
    header: 'Total Collateral',
    accessorFn: originalRow => amountToAccessorValue(originalRow.collateral.amount.value),
    id: 'totalCollateral',
    columnFilterModeOptions: numberFilters,
    enableColumnActions: false,
    sortingFn: 'basic',
    minSize: 215,
    maxSize: 245,
    filterFn: numberFilters[0],
    Cell: ({ row, table }) => {
      return (
        <FallbackedTotalUSDWithProgress
          {...row.original.collateral.amount}
          formattedAmountProps={shortFormattedAmountProps}
          progress={row.original.collateral.progress}
          displayTokenAmounts={
            table.getState().displaySettings?.fallbackedTotalUSDVariant === 'fallback'
          }
        />
      );
    },
    muiTableHeadCellProps: {
      sx: headCellAlignStyles,
    },
    muiTableBodyCellProps: {
      align: 'right',
    },
  },
  ToggleLowSupplyFilterButton.getLowSupplyColumn(),
];

const TokenNameBox = styled('div')({
  display: 'flex',
  gap: 8,
  alignItems: 'center',
});

const TooltipTitle = styled('h3')({
  margin: 0,
  fontWeight: 'bold',
});

const TooltipDescription = styled('p')({
  margin: 0,
});
